import { createContext, useContext } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * The Create tabs and title live in the shared `CreatePage` header, but the
 * per-tab controls (New Idea, Generate Ideas, Search, Filters, Add Feed, …)
 * live in the individual tab pages rendered through `<Outlet />`. These slots
 * let each page render its controls into the shared header while keeping their
 * own state and hooks: the controls stay in the page's React tree (so context
 * and callbacks work as usual) and are only portaled into the header's DOM.
 */
type CreateHeaderSlotsValue = {
  /** Right of the title, next to the feedback widget. */
  primaryAction: HTMLElement | null
  /** End of the tabs row, right-aligned. */
  tabsActions: HTMLElement | null
  /** Row below the tabs separator, for a page-specific sub-navigation. */
  subnav: HTMLElement | null
}

const CreateHeaderSlotsContext = createContext<CreateHeaderSlotsValue | null>(
  null,
)

export const CreateHeaderSlotsProvider = CreateHeaderSlotsContext.Provider

function useCreateHeaderSlots(): CreateHeaderSlotsValue {
  const slots = useContext(CreateHeaderSlotsContext)
  if (!slots) {
    throw new Error('Create header slots must be used within CreatePage')
  }
  return slots
}

export function CreatePrimaryAction({
  children,
}: {
  children: ReactNode
}): ReactNode {
  const { primaryAction } = useCreateHeaderSlots()
  return primaryAction ? createPortal(children, primaryAction) : null
}

export function CreateTabsActions({
  children,
}: {
  children: ReactNode
}): ReactNode {
  const { tabsActions } = useCreateHeaderSlots()
  return tabsActions ? createPortal(children, tabsActions) : null
}

export function CreateSubnav({ children }: { children: ReactNode }): ReactNode {
  const { subnav } = useCreateHeaderSlots()
  return subnav ? createPortal(children, subnav) : null
}
